import compact from 'lodash/compact'
import { useMemo, useState } from 'react'
import { useUser } from '../useUser'
import { useMutation } from '../useMutation'
import type { UseListProps, UseListResult } from '../useList'
import { useSubscription } from '../useSubscription'
import type { Notification, EntityMeta } from '@ztechapps/common.contract'
import { useDocument } from '../useDocument'
import { useNotificationList } from './useNotificationList'
import { countQuery, notifcationQuery } from './graphql'

export type UseNotification = {
  notifications: UseListResult<Notification>
  handleIsRead: (filter: any) => Promise<void>
  handleIsReadAll: () => Promise<void>
  unreadCount: number
  notification: Notification
  handleUpdate: (args: { filter: any; input: any }) => Promise<void>
  handleUpdateMany: (args: { filter: any; input: any }) => Promise<void>
  filter: any
  loading: boolean
}

export type UseNotifcationProps = { listProps?: Partial<UseListProps<Notification>> }

export const useNotification = ({ listProps }: UseNotifcationProps): UseNotification => {
  const { user } = useUser()

  const scopeFilter = {
    $or: [
      { 'scope.key': 'user', 'scope.value': user?._id },
      { 'scope.key': 'tenant', 'scope.value': user?.tenantId },
    ],
  }

  let newFilter = [scopeFilter]
  newFilter = compact(newFilter)
  const filter = { $and: newFilter }

  const [update, , { loading: updateLoading }] = useMutation({ ...notifcationQuery.updateConfig })
  const [updateMany, , { loading: updateManyLoading }] = useMutation({ ...notifcationQuery.updateManyConfig })
  const notifications = useNotificationList(listProps ?? {})

  const unreadList = useDocument<EntityMeta>({ query: countQuery, filter: { ...scopeFilter, isRead: false } })

  const latestNotification = useMemo<Notification>(() => {
    return notifications?.list?.[0]
  }, [notifications?.list])

  const unreadCount = useMemo<number>(() => {
    return unreadList?.document?.count
  }, [unreadList?.document?.count])

  useSubscription({
    query: notifcationQuery.subscription.created.query,
    onData: () => {
      notifications.refetch()
      unreadList.refetch()
    },
  })

  useSubscription({
    query: notifcationQuery.subscription.updated.query,
    onData: () => {
      unreadList.refetch()
    },
  })

  const [updating, setUpdating] = useState(false)

  //for updating single notification status to Read
  const handleIsRead = async ({ filter = {} }) => {
    setUpdating(true)

    const updateFilter = { ...filter, isRead: false }
    const input = { isRead: true }
    await update?.({ input, filter: updateFilter })

    setUpdating(false)
  }

  const handleUpdate = async ({ filter = {}, input }) => {
    setUpdating(true)
    const updateFilter = { ...scopeFilter, ...filter }

    await update?.({ input, filter: updateFilter })

    setUpdating(false)
  }

  const handleUpdateMany = async ({ filter = {}, input }) => {
    setUpdating(true)

    const updateFilter = { ...scopeFilter, ...filter }
    await updateMany?.({ input, filter: updateFilter })

    //since there is no subscript for updateMany, we need to refetch the list
    notifications.refetch()
    unreadList.refetch()
    setUpdating(false)
  }

  //for updating many notification status to Read
  const handleIsReadAll = async () => {
    setUpdating(true)
    const updateFilter = { ...scopeFilter, isRead: { $ne: true } }

    const input = { isRead: true }
    await updateMany?.({ input, filter: updateFilter })

    //since there is no subscript for updateMany, we need to refetch the list
    notifications.refetch()
    unreadList.refetch()
    setUpdating(false)
  }

  const loading = useMemo(() => {
    return updating || updateLoading || updateManyLoading
  }, [updating, updateLoading, updateManyLoading, notifications?.loading])

  return {
    notification: latestNotification,
    handleIsRead,
    notifications,
    handleIsReadAll,
    unreadCount,
    handleUpdate,
    filter,
    handleUpdateMany,
    loading,
  }
}
